import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

/**
 * The five steps, with where you are among them.
 *
 * A step already behind you is a button back to it. Steps ahead are not, even
 * ones reached before: each page saves on Continue, and jumping forward from a
 * page with unsaved edits would leave them behind without a word.
 *
 * Step titles are hidden below `sm` — five of them side by side wrap into an
 * unreadable mess on a phone, so small screens get the numbered circles and
 * the current step's name only.
 */
const WizardHeader = ({ steps, currentStep, onStepSelect }) => {
  const { t } = useTranslation();
  const current = steps.find((s) => s.number === currentStep);

  return (
    <div className="px-4 pt-5 pb-1 sm:px-8 sm:pt-6">
      <ol className="m-0 mx-auto flex max-w-2xl list-none items-start p-0">
        {steps.map((step, index) => {
          const isActive = step.number === currentStep;
          const isComplete = step.number < currentStep;
          const circle = (
            <span
              className={`flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold transition ${
                isActive
                  ? "border-brand-600 bg-brand-600 text-white"
                  : isComplete
                    ? "border-brand-600 bg-white text-brand-700"
                    : "border-ink-300 bg-white text-ink-500"
              }`}
            >
              {isComplete ? (
                <i className="bi bi-check-lg text-sm" aria-hidden="true" />
              ) : (
                step.number
              )}
            </span>
          );
          const label = (
            <span
              className={`hidden max-w-24 text-center text-xs sm:block ${
                isActive ? "font-semibold text-brand-700" : "text-ink-500"
              }`}
            >
              {step.title}
            </span>
          );

          return (
            <React.Fragment key={step.number}>
              <li
                className="flex min-w-0 flex-col items-center"
                aria-current={isActive ? "step" : undefined}
              >
                {isComplete && onStepSelect ? (
                  <button
                    type="button"
                    onClick={() => onStepSelect(step.number)}
                    aria-label={t("Dashboard.Setup.backToStep", {
                      title: step.title,
                    })}
                    className="flex min-h-11 min-w-11 cursor-pointer flex-col items-center gap-1.5 rounded-lg px-1 transition hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                  >
                    {circle}
                    {label}
                  </button>
                ) : (
                  <span className="flex min-h-11 min-w-11 flex-col items-center gap-1.5 px-1">
                    {circle}
                    {label}
                  </span>
                )}
              </li>
              {index < steps.length - 1 && (
                <li
                  aria-hidden="true"
                  className={`mx-0.5 mt-4 h-0.5 min-w-3 flex-1 rounded-full transition ${
                    isComplete ? "bg-brand-600" : "bg-ink-200"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </ol>

      {/* Small screens hide the labels above, so name the current step here. */}
      <p className="m-0 mt-2 text-center text-xs font-semibold text-brand-700 sm:hidden">
        {t("Dashboard.Wizard.stepOf", {
          current: currentStep,
          total: steps.length,
          title: current?.title,
        })}
      </p>
    </div>
  );
};

WizardHeader.propTypes = {
  steps: PropTypes.array.isRequired,
  currentStep: PropTypes.number.isRequired,
  /** Called with a completed step's number when it is pressed. */
  onStepSelect: PropTypes.func,
};

export default WizardHeader;
